'use strict';
import classNames from 'classnames';
import isDate from 'lodash/isDate';
import isNumber from 'lodash/isNumber';
import memoize from 'lodash/memoize';
import moment from 'moment';
import * as T from 'prop-types';
import React from 'react';
import {CommentIndicator} from '../../../comments/components/CommentIndicator';
import {ListItem} from '../../../components/lists/ListItem';
import {StatelessStatusSelector} from '../../../core-components/selectors/components/StatelessStatusSelector';
import {getCollection, getId} from '../../../core-utilities/domain/ReduxUtils';
import {getMessagesFromProps} from '../../../core-utilities/react-i18n';
import {stopPropagation} from '../../../core-utilities/react-utils';
import {TaskAssignment} from '../../../tasks/components/TaskAssignment';
import {TaskProgress} from '../../../tasks/components/TaskProgress';
import {ScheduleModalMode} from '../../../tasks/enums/ScheduleModalMode';
import {TaskStatus} from '../../../tasks/enums/TaskStatus';
import {PinButton} from '../PinButton';


export const cachedFmtDate = memoize(fmtDate);
export const cachedFmtTime = memoize(fmtTime);

export function UserPinView(props) {
  const target = props.target;
  const date = cachedFmtDate(props.taskDate);
  const itemTitle = getPinTitle(props);
  return (
    <ListItem
      className={classNames('UserPin', props.className, props.scheduleStatus)}
      bindParam={props.model}
      onClick={props.onClick}
      onContextMenu={props.onContextMenu}
    >
      <div
        className="UserPinLeft UserPinLeft_large"
        onClick={stopPropagation}
      >
        <div className={classNames('UserPinLeft__wrapper', props.taskStatus)}>
          <StatelessStatusSelector
            model={target}
            status={target.status}
            validStatuses={target.validStatuses}
            hideValue={true}
          />
        </div>
      </div>
      <div className="UserPinLeft UserPinLeft_small">
        <div className={classNames('UserPinLeft__wrapper', props.taskStatus)}>
          <div>
            {renderDateLabel(props)}
          </div>
          {date ? (
            <div>
              <div>
                {date}
              </div>
              {!props.useTime ? null : (
                <div className="UserPinLeft__time">
                  {cachedFmtTime(props.taskDate)}
                </div>
              )}
            </div>
          ) : (
            <div>
              {props.defaultNALabel}
            </div>
          )}
        </div>
      </div>

      <div className="UserPinCenter">
        <div
          className="UserPinCenter__content"
          title={itemTitle}
        >
          <div
            className="UserPinCenter__title"
          >
            <span className="UserPinCenter__badge task">
              {`${props.userHomeBadgeTaskLabel}:`}
            </span>
            {` ${props.targetName}`}
          </div>
          <div className="UserPinCenter__parent">
            <i className="icon ion-ios-arrow-back" />
            <span className="UserPinCenter__parent_org">
              {`${props.organizationName} / `}
            </span>
            <span>
              {props.parentName}
            </span>
          </div>
          <div className="UserPinCenter__progress-wrapper">
            <div className="UserPinCenter__progress">
              <TaskProgress
                status={props.taskStatus}
                {...getMessagesFromProps(props, TaskProgress.messageProps)}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className="UserPin__assignmentWrapper"
        onClick={stopPropagation}
      >
        <TaskAssignment
          className="UserPin__assignment"
          scheduleModalMode={ScheduleModalMode.foreign}
          model={target}

          init={props.init}
          {...getMessagesFromProps(props, TaskAssignment.messageProps)}
        />
      </div>

      <div className="UserPinRight">
        {/* hide at small size*/}
        <div className="UserPinRight__metrics">
          <div className="UserPinRight__metrics__container">
            <i className="icon ion-ios-folder-outline" />
            <span className="UserPinRight__metrics__label">
              {props.fileCountLabel}
            </span>
          </div>
        </div>

        {/* vertical at small size, horizontal at larger size*/}
        <div
          className="UserPinRight__buttons"
          onClick={stopPropagation}
        >
          <div className="UserPinRight__buttons_comment">
            <CommentIndicator
              userId={props.userId}
              chatId={getId(target)}
              collection={getCollection(target)}
              allowNav={true}

              init={props.init}
              {...getMessagesFromProps(props, CommentIndicator.messageProps)}
            />
          </div>
          <PinButton
            className="UserPinRight__buttons_pin"
            onClick={props.onTogglePin}
            isPinned={props.isPinned}
            {...getMessagesFromProps(props, PinButton.messageProps)}
          />
        </div>
      </div>
    </ListItem>
  );
}

function getPinTitle(props) {
  return `${props.targetName}\n\n${props.parentName}`;
}

function renderDateLabel(props) {
  if (props.taskStatus === TaskStatus.submitted) {
    return (
      <i className="UserPinLeft__icon UserPinLeft__icon_submitted icon swift-submitted" />
    );
  }

  switch (props.selectedDateLabel) {
    case 'due':
      return (
        <span className="UserPinLeft__label">
          {props.defaultDueLabel}
        </span>
      );
    case 'complete':
      return (
        <i className="UserPinLeft__icon UserPinLeft__icon_complete icon ion-ios-checkmark-empty" />
      );
    case 'cancelled':
      return (
        <i className="UserPinLeft__icon UserPinLeft__icon_cancelled icon ion-ios-close-empty" />
      );
    default:
      return null;
  }
}

function fmtDate(date) {
  return isDate(date) || isNumber(date)
    ? moment(date).format('M/D')
    : null;
}

function fmtTime(date) {
  return isDate(date) || isNumber(date)
    ? moment(date).format('H:mm')
    : null;
}

UserPinView.propTypes = {
  targetName: T.string,
  description: T.string,
  parentName: T.string,

  selectedDateLabel: T.oneOf(['due', 'complete', 'cancelled']),
  taskStatus: T.string,
  taskDate: T.any, // one of Date, Number, or null
  scheduleStatus: T.string,
  useTime: T.bool,

  onClick: T.func.isRequired,
  onContextMenu: T.func.isRequired,
  onTogglePin: T.func.isRequired,

  userHomeBadgeTaskLabel: T.string,
  defaultDueLabel: T.string,
  defaultNALabel: T.string,

  taskAssignment: T.element,
};

UserPinView.messages = [
  {code: 'user.home.badge.task', prop: 'userHomeBadgeTaskLabel'},
  {code: 'default.due.label', prop: 'defaultDueLabel'},
  {code: 'default.na.label', prop: 'defaultNALabel'},
  ...CommentIndicator.messages,
  ...PinButton.messages,
  ...TaskAssignment.messages,
  ...TaskProgress.messages,
];

UserPinView.messageProps = [
  'userHomeBadgeTaskLabel',
  'defaultDueLabel',
  'defaultNALabel',
  ...CommentIndicator.messageProps,
  ...PinButton.messageProps,
  ...TaskAssignment.messageProps,
  ...TaskProgress.messageProps,
];



// WEBPACK FOOTER //
// ./src/js/priorities/components/UserPin/UserPinView.js